import { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import {
  FileText, X, Download, FileSpreadsheet, Loader2, Save, RotateCcw,
  Plus, Trash2, Eye, EyeOff,
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/api';
import { JURISDICTION_NAMES } from '../../lib/constants';
import { JURISDICTION_FLAGS, formatDate, getScoreColor, getScoreLevel } from './adminUtils';

const PRIORITY_OPTIONS = ['HIGH', 'MEDIUM', 'LOW'];
const TIMELINE_OPTIONS = ['Immediate', '1-3 months', '3-6 months', 'As needed', 'Ongoing'];

const priorityBadgeClass = (priority) => {
  if (priority === 'HIGH') return 'bg-red-500/10 text-red-400 border-red-500/30';
  if (priority === 'MEDIUM') return 'bg-yellow-500/10 text-yellow-400 border-yellow-500/30';
  return 'bg-slate-500/10 text-slate-400 border-slate-500/30';
};

export const ActionPlanEditorModal = ({
  data, // { assessment, invitation, jurisdiction_info } from /admin/guest-assessments/:id
  loading,
  onClose,
  onDownloadWord,
  onDownloadExcel,
  downloadingWord,
  downloadingExcel,
  headers,
  onSaved,
}) => {
  const [draftPlan, setDraftPlan] = useState([]);
  const [showOriginal, setShowOriginal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [resetting, setResetting] = useState(false);

  const assessment = data?.assessment;
  const invitation = data?.invitation;
  const originalPlan = assessment?.action_plan || [];
  const editedPlan = assessment?.edited_action_plan;
  const hasEdits = Boolean(editedPlan && editedPlan.length);
  const sourcePlan = hasEdits ? editedPlan : originalPlan;

  useEffect(() => {
    if (assessment) {
      setDraftPlan(sourcePlan.map((item) => ({ ...item })));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assessment?.id, hasEdits]);

  if (!data) return null;

  const guest = assessment?.guest_info || {};
  const internal = assessment?.internal_scores || {};

  const updateItem = (idx, field, value) => {
    setDraftPlan((prev) => {
      const next = [...prev];
      next[idx] = { ...next[idx], [field]: value };
      return next;
    });
  };

  const removeItem = (idx) => {
    setDraftPlan((prev) => prev.filter((_, i) => i !== idx));
  };

  const addItem = () => {
    setDraftPlan((prev) => [
      ...prev,
      { category: 'Custom', priority: 'MEDIUM', action: '', timeline: '1-3 months' },
    ]);
  };

  const movePriority = (priority) => priority === 'HIGH' ? 0 : priority === 'MEDIUM' ? 1 : 2;
  const sortedDraft = [...draftPlan].map((item, i) => ({ ...item, _i: i }))
    .sort((a, b) => movePriority(a.priority) - movePriority(b.priority));

  const handleSave = async () => {
    // Validate: every item needs non-empty action text
    const empty = draftPlan.findIndex((it) => !it.action?.trim());
    if (empty !== -1) {
      toast.error('All action items need text. Remove or fill the empty one.');
      return;
    }
    setSaving(true);
    try {
      await api.patch(
        `/admin/guest-assessments/${assessment.id}/action-plan`,
        { action_plan: draftPlan },
        { headers },
      );
      toast.success('Action plan saved');
      onSaved?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save action plan');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    // eslint-disable-next-line no-alert
    if (!window.confirm('Revert to the system-generated action plan? Your edits will be discarded.')) return;
    setResetting(true);
    try {
      await api.delete(`/admin/guest-assessments/${assessment.id}/action-plan`, { headers });
      toast.success('Reverted to system-generated plan');
      onSaved?.();
    } catch {
      toast.error('Failed to revert');
    } finally {
      setResetting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4" data-testid="action-plan-editor-modal">
      <div className="bg-slate-800 rounded-xl border border-slate-700 w-full max-w-5xl max-h-[95vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="bg-slate-800 border-b border-slate-700 p-4 flex items-center justify-between flex-shrink-0">
          <div className="flex items-center gap-3">
            <span className="text-2xl">{JURISDICTION_FLAGS[assessment?.jurisdiction]}</span>
            <div>
              <h2 className="text-lg font-bold text-white">{guest.company_name || guest.name || 'Guest Assessment'}</h2>
              <p className="text-slate-400 text-xs">
                {JURISDICTION_NAMES[assessment?.jurisdiction]} • {guest.email} • Completed {formatDate(assessment?.completed_at)}
              </p>
            </div>
          </div>
          <Button variant="ghost" size="sm" onClick={onClose} className="text-slate-400 hover:text-white" data-testid="close-action-plan-editor">
            <X className="h-5 w-5" />
          </Button>
        </div>

        {loading || !assessment ? (
          <div className="p-12 flex justify-center">
            <Loader2 className="h-8 w-8 animate-spin text-[#3B82F6]" />
          </div>
        ) : (
          <div className="flex-1 overflow-y-auto p-6 space-y-6">
            {/* Score summary */}
            <div className="bg-slate-700/30 rounded-lg p-4 grid grid-cols-2 md:grid-cols-5 gap-4 text-center">
              <Stat label="Score" value={`${(internal.overall_score || 0).toFixed(0)}%`} accent="#3B82F6" />
              <Stat label="Rating" value={internal.overall_rating?.rating || 'N/A'} accent="#8B5CF6" />
              <Stat label="Strong" value={internal.strong_count || 0} accent="#10B981" />
              <Stat label="Weak" value={internal.weak_count || 0} accent="#EF4444" />
              <Stat label="Gaps" value={internal.gap_count || 0} accent="#94A3B8" />
            </div>

            {/* Invitation meta */}
            <div className="bg-slate-700/30 rounded-lg p-4">
              <p className="text-slate-400 text-xs mb-2 uppercase">Client information</p>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-sm">
                <Field label="Industry" value={invitation?.industry} />
                <Field label="Contact" value={guest.name} />
                <Field label="Phone" value={guest.phone || invitation?.phone} />
                <Field label="Email" value={guest.email} />
                <Field label="Engagement" value={invitation?.engagement_status || 'prospect'} />
                <Field label="Invitation sent" value={invitation?.created_at ? formatDate(invitation.created_at) : '—'} />
              </div>
              {invitation?.internal_notes && (
                <div className="mt-3 pt-3 border-t border-slate-600/40">
                  <p className="text-slate-400 text-xs mb-1 uppercase">Internal notes</p>
                  <p className="text-slate-200 text-sm whitespace-pre-wrap">{invitation.internal_notes}</p>
                </div>
              )}
            </div>

            {/* Action Plan Editor */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <div>
                  <h3 className="text-white font-semibold">Action Plan</h3>
                  <p className="text-slate-400 text-xs">
                    Reword, reprioritise, add or delete items. The Word download uses your edited version.
                    {hasEdits && (
                      <span className="ml-2 text-[#10B981]">• Edited {formatDate(assessment.action_plan_edited_at)}</span>
                    )}
                  </p>
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setShowOriginal((v) => !v)}
                    className="border-slate-600 text-slate-300 hover:bg-slate-700"
                    data-testid="toggle-original-btn"
                  >
                    {showOriginal ? <EyeOff className="h-4 w-4 mr-1" /> : <Eye className="h-4 w-4 mr-1" />}
                    {showOriginal ? 'Hide' : 'View'} system-generated
                  </Button>
                  {hasEdits && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleReset}
                      disabled={resetting}
                      className="border-slate-600 text-slate-300 hover:bg-slate-700"
                      data-testid="reset-action-plan-btn"
                    >
                      {resetting ? <Loader2 className="h-4 w-4 animate-spin" /> : <RotateCcw className="h-4 w-4 mr-1" />}
                      Revert
                    </Button>
                  )}
                </div>
              </div>

              {/* Original (read-only) panel */}
              {showOriginal && (
                <div className="bg-slate-700/20 border border-slate-700 rounded-lg p-3 mb-4">
                  <p className="text-slate-400 text-xs mb-2 uppercase">System-Generated Original (read-only, for reference)</p>
                  <div className="space-y-2 max-h-60 overflow-y-auto">
                    {originalPlan.length === 0 ? (
                      <p className="text-slate-500 text-sm">No original action plan available.</p>
                    ) : (
                      originalPlan.map((item) => (
                        <div key={`orig-${item.category}-${item.action}`} className="flex items-start gap-3 p-2 bg-slate-800/30 rounded text-sm">
                          <Badge className={priorityBadgeClass(item.priority)} variant="outline">{item.priority}</Badge>
                          <div className="flex-1">
                            <p className="text-slate-200">{item.action}</p>
                            <p className="text-slate-500 text-xs">{item.category} • {item.timeline}</p>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              )}

              {/* Editor */}
              <div className="space-y-3" data-testid="action-plan-editor-list">
                {sortedDraft.length === 0 ? (
                  <p className="text-slate-500 text-sm text-center py-6 bg-slate-700/20 rounded-lg">
                    No action items yet. Click "Add action item" to start.
                  </p>
                ) : (
                  sortedDraft.map((item) => (
                    <div
                      key={`draft-${item._i}`}
                      className="bg-slate-700/30 rounded-lg p-3 flex items-start gap-3"
                      data-testid={`action-row-${item._i}`}
                    >
                      <div className="flex flex-col gap-2 w-32 flex-shrink-0">
                        <Select
                          value={item.priority}
                          onValueChange={(v) => updateItem(item._i, 'priority', v)}
                        >
                          <SelectTrigger className="bg-slate-700 border-slate-600 text-white h-8 text-xs">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {PRIORITY_OPTIONS.map((p) => (
                              <SelectItem key={p} value={p}>{p}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <Select
                          value={item.timeline}
                          onValueChange={(v) => updateItem(item._i, 'timeline', v)}
                        >
                          <SelectTrigger className="bg-slate-700 border-slate-600 text-white h-8 text-xs">
                            <SelectValue>{item.timeline || 'Select…'}</SelectValue>
                          </SelectTrigger>
                          <SelectContent>
                            {TIMELINE_OPTIONS.map((t) => (
                              <SelectItem key={t} value={t}>{t}</SelectItem>
                            ))}
                            {item.timeline && !TIMELINE_OPTIONS.includes(item.timeline) && (
                              <SelectItem value={item.timeline}>{item.timeline} (custom)</SelectItem>
                            )}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="flex-1 space-y-2">
                        <Input
                          value={item.category}
                          onChange={(e) => updateItem(item._i, 'category', e.target.value)}
                          placeholder="Category"
                          className="bg-slate-700 border-slate-600 text-slate-300 h-8 text-xs"
                        />
                        <textarea
                          value={item.action}
                          onChange={(e) => updateItem(item._i, 'action', e.target.value)}
                          placeholder="Action item text..."
                          rows={2}
                          className="w-full bg-slate-700 border border-slate-600 text-white rounded-md p-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#3B82F6]"
                          data-testid={`action-text-${item._i}`}
                        />
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => removeItem(item._i)}
                        className="text-slate-500 hover:text-red-400 hover:bg-red-500/10 flex-shrink-0"
                        data-testid={`delete-action-${item._i}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  ))
                )}
              </div>

              <Button
                variant="outline"
                onClick={addItem}
                data-testid="add-action-btn"
                className="mt-3 w-full border-dashed border-slate-600 text-slate-300 hover:bg-slate-700"
              >
                <Plus className="h-4 w-4 mr-2" />
                Add action item
              </Button>
            </div>
          </div>
        )}

        {/* Footer actions */}
        <div className="border-t border-slate-700 p-4 flex items-center justify-between flex-shrink-0 bg-slate-800/50">
          <Badge className={getScoreColor(getScoreLevel(internal.overall_score || 0))}>
            Overall {(internal.overall_score || 0).toFixed(0)}%
          </Badge>
          <div className="flex gap-2 flex-wrap">
            <Button
              variant="outline"
              onClick={() => onDownloadExcel(assessment)}
              disabled={downloadingExcel === assessment?.id}
              data-testid="modal-download-excel-btn"
              className="border-[#10B981] text-[#10B981] hover:bg-[#10B981]/10"
            >
              {downloadingExcel === assessment?.id ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <FileSpreadsheet className="h-4 w-4 mr-2" />
              )}
              Excel Q&A
            </Button>
            <Button
              variant="outline"
              onClick={() => onDownloadWord(assessment)}
              disabled={downloadingWord === assessment?.id}
              data-testid="modal-download-word-btn"
              className="border-[#3B82F6] text-[#3B82F6] hover:bg-[#3B82F6]/10"
            >
              {downloadingWord === assessment?.id ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Download className="h-4 w-4 mr-2" />
              )}
              Word Report
            </Button>
            <Button
              onClick={handleSave}
              disabled={saving}
              data-testid="save-action-plan-btn"
              className="bg-[#10B981] hover:bg-[#059669] text-white"
            >
              {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
              Save plan
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

const Stat = ({ label, value, accent }) => (
  <div>
    <p className="text-2xl font-bold" style={{ color: accent }}>{value}</p>
    <p className="text-slate-400 text-xs">{label}</p>
  </div>
);

const Field = ({ label, value }) => (
  <div>
    <p className="text-slate-400 text-xs">{label}</p>
    <p className="text-slate-200 truncate" title={value}>{value || '—'}</p>
  </div>
);
